import { useMemo } from 'react'
import { Check, MapPin, User } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useDistrictsByNation } from '@/hooks/useDistrictsByNation'
import { useRegionalCandidates } from '@/hooks/useRegionalCandidates'
import { useRegionsByNation } from '@/hooks/useRegionsByNation'
import { cn } from '@/lib/utils'
import type { District, Region, CandidateWithDistrict } from '@/types'

interface DistrictSelectorProps {
  nationId: string
  partyId: string
  selectedDistrictId: string | undefined
  onChange: (districtId: string | undefined) => void
  currentCandidateId?: string
  className?: string
}

interface DistrictWithAssignment extends District {
  regionName: string
  assignedCandidate: CandidateWithDistrict | null
}

interface DistrictGroup {
  region: Region
  districts: DistrictWithAssignment[]
}

function formatSettlementType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1)
}

export function DistrictSelector({
  nationId,
  partyId,
  selectedDistrictId,
  onChange,
  currentCandidateId,
  className,
}: DistrictSelectorProps) {
  const { districts, isLoading: districtsLoading } = useDistrictsByNation(nationId)
  const { regions, isLoading: regionsLoading } = useRegionsByNation(nationId)
  const { candidates: regionalCandidates, isLoading: candidatesLoading } =
    useRegionalCandidates(partyId)

  const isLoading = districtsLoading || regionsLoading || candidatesLoading

  // Group districts by region and include assignment info
  const districtGroups = useMemo(() => {
    if (!districts || !regions) return []

    const regionMap = new Map(regions.map((r) => [r.id, r]))
    const candidateByDistrict = new Map(
      regionalCandidates?.map((c) => [c.assigned_district_id, c]) ?? []
    )

    // Group districts by region
    const groups = new Map<string, DistrictWithAssignment[]>()

    for (const district of districts) {
      const region = regionMap.get(district.region_id)
      if (!region) continue

      const districtWithAssignment: DistrictWithAssignment = {
        ...district,
        regionName: region.name,
        assignedCandidate: candidateByDistrict.get(district.id) ?? null,
      }

      const existing = groups.get(district.region_id)
      if (existing) {
        existing.push(districtWithAssignment)
      } else {
        groups.set(district.region_id, [districtWithAssignment])
      }
    }

    // Convert to array and sort
    const result: DistrictGroup[] = []
    for (const [regionId, districtList] of groups) {
      const region = regionMap.get(regionId)
      if (region) {
        result.push({
          region,
          districts: districtList.sort((a, b) => a.name.localeCompare(b.name)),
        })
      }
    }

    return result.sort((a, b) => a.region.name.localeCompare(b.region.name))
  }, [districts, regions, regionalCandidates])

  const selectedDistrict = useMemo(() => {
    if (!selectedDistrictId || !districts) return null
    return districts.find((d) => d.id === selectedDistrictId) ?? null
  }, [selectedDistrictId, districts])

  const assignedCandidate = useMemo(() => {
    if (!selectedDistrictId || !regionalCandidates) return null
    return (
      regionalCandidates.find((c) => c.assigned_district_id === selectedDistrictId) ?? null
    )
  }, [selectedDistrictId, regionalCandidates])

  if (isLoading) {
    return (
      <div className={cn('space-y-2', className)}>
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
      </div>
    )
  }

  if (!districts || districts.length === 0) {
    return (
      <div className={cn('text-sm text-muted-foreground', className)}>
        No districts available in this nation.
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      <Select
        value={selectedDistrictId ?? ''}
        onValueChange={(value) => onChange(value || undefined)}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select a district...">
            {selectedDistrict && (
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                {selectedDistrict.name}
              </span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-80">
          {districtGroups.map((group) => (
            <div key={group.region.id}>
              <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground bg-muted/50">
                {group.region.name}
              </div>
              {group.districts.map((district) => {
                const isAssignedToOther =
                  district.assignedCandidate &&
                  district.assignedCandidate.id !== currentCandidateId
                const isAssignedToSelf =
                  district.assignedCandidate?.id === currentCandidateId

                return (
                  <SelectItem
                    key={district.id}
                    value={district.id}
                    className="pl-4"
                  >
                    <div className="flex items-center justify-between gap-3 w-full">
                      <div className="flex items-center gap-2">
                        <span>{district.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({formatSettlementType(district.settlement_type)})
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-xs">
                        {isAssignedToSelf ? (
                          <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                            <Check className="h-3 w-3" />
                            Current
                          </span>
                        ) : isAssignedToOther ? (
                          <span className="text-amber-600 dark:text-amber-400 flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {district.assignedCandidate?.name}
                          </span>
                        ) : (
                          <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                            <Check className="h-3 w-3" />
                            Available
                          </span>
                        )}
                      </div>
                    </div>
                  </SelectItem>
                )
              })}
            </div>
          ))}
        </SelectContent>
      </Select>

      {/* Show warning if replacing another candidate */}
      {assignedCandidate && assignedCandidate.id !== currentCandidateId && (
        <div className="flex items-start gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400">
          <User className="h-4 w-4 mt-0.5 shrink-0" />
          <p className="text-sm">
            <span className="font-medium">{assignedCandidate.name}</span> is
            currently assigned to this district. They will be set to Unassigned.
          </p>
        </div>
      )}
    </div>
  )
}
